import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Plus, Filter, Search, TrendingUp, TrendingDown, Target, ChevronDown, Sparkles, X, GitMerge, BarChart4, Shield, Save, FileDown } from 'lucide-react';
import { projectRaidService } from '../../services/projectRaid';
import { aiInsightsService } from '../../services/aiInsights';
import { OwnerSelect } from '../common/OwnerSelect';
import { loadAnalysis, saveAnalysis } from './analysisStore';
import { exportAnalysisPdf } from './exportAnalysisPdf';

function parseRaidId(displayId: string): number | null {
  const m = /([0-9]+)/.exec(displayId);
  return m ? parseInt(m[1], 10) : null;
}
import { toast } from 'sonner';

interface Risk {
  id: string;
  title: string;
  description: string;
  category: string;
  probability: string;
  impact: string;
  score: number;
  status: string;
  owner: string;
  dateIdentified: string;
  targetDate: string;
  mitigation: string;
  trend: string;
}

interface RisksSectionProps {
  projectId?: number;
  onChanged?: () => void;
}

const RISK_LEVELS = ['Low', 'Medium', 'High'] as const;
function levelToScoreFactor(level: string): number {
  if (level === 'High') return 4;
  if (level === 'Low') return 1;
  return 2; // Medium
}
function computeRiskScore(probability: string, impact: string): number {
  return levelToScoreFactor(probability) * levelToScoreFactor(impact);
}

function mapRaidItemToRisk(item: { id: number; title: string | null; description: string; status: string | null; owner: string | null; due_date: string | null; extra_metadata: Record<string, unknown> | null }): Risk {
  const meta = item.extra_metadata || {};
  return {
    id: `R-${String(item.id).padStart(3, '0')}`,
    title: item.title || item.description.slice(0, 50) || 'Risk',
    description: item.description,
    category: (meta.category as string) || 'Other',
    probability: (meta.probability as string) || 'Medium',
    impact: (meta.impact as string) || 'Medium',
    score: typeof meta.score === 'number' ? meta.score : 8,
    status: item.status || 'Open',
    owner: item.owner || '',
    dateIdentified: (meta.dateIdentified as string) || '',
    targetDate: item.due_date || '',
    mitigation: (meta.mitigation as string) || '',
    trend: (meta.trend as string) || 'stable',
  };
}

export function RisksSection({ projectId, onChanged }: RisksSectionProps) {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null);
  const [analysisMethod, setAnalysisMethod] = useState<'fmea' | 'montecarlo' | 'bowtie' | null>(null);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [showForm, setShowForm] = useState(false);

  const loadRisks = useCallback(() => {
    if (!projectId) {
      setRisks([]);
      return;
    }
    projectRaidService
      .list(projectId, 'risk')
      .then((list) => setRisks(list.map(mapRaidItemToRisk)))
      .catch(() => {
        toast.error('Failed to load risks');
        // Do NOT fall back to demo data on real projects — a failed load
        // showing fake names (Sarah Chen et al.) is worse than an empty
        // state because customers can't tell it's a fallback.
        setRisks([]);
      });
  }, [projectId]);

  useEffect(() => {
    loadRisks();
  }, [loadRisks]);

  const handleCreateRisk = async (data: {
    title: string;
    description: string;
    category: string;
    probability: string;
    impact: string;
    owner: string;
    targetDate: string;
    mitigation: string;
  }) => {
    if (!projectId) {
      toast.error('Save the project before adding risks');
      return;
    }
    const score = computeRiskScore(data.probability, data.impact);
    try {
      await projectRaidService.create(projectId, {
        raid_type: 'risk',
        title: data.title,
        description: data.description,
        status: 'Open',
        owner: data.owner || null,
        due_date: data.targetDate || null,
        extra_metadata: {
          category: data.category,
          probability: data.probability,
          impact: data.impact,
          score,
          mitigation: data.mitigation,
          dateIdentified: new Date().toISOString().slice(0, 10),
          trend: 'stable',
        },
      });
      toast.success('Risk added');
      setShowForm(false);
      loadRisks();
      onChanged?.();
    } catch {
      toast.error('Failed to add risk');
    }
  };

  const getPriorityColor = (score: number) => {
    if (score >= 15) return 'bg-red-100 text-red-700 border-red-200';
    if (score >= 8) return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  };

  const getPriorityLabel = (score: number) => {
    if (score >= 15) return 'Critical';
    if (score >= 8) return 'High';
    return 'Medium';
  };

  const riskStats = {
    total: risks.length,
    critical: risks.filter(r => r.score >= 15).length,
    high: risks.filter(r => r.score >= 8 && r.score < 15).length,
    medium: risks.filter(r => r.score < 8).length,
    open: risks.filter(r => r.status === 'Open').length,
  };

  const handleOpenAnalysis = (method: 'fmea' | 'montecarlo' | 'bowtie') => {
    if (!selectedRisk) {
      toast.error('Please select a risk first before using the analysis technique.');
      return;
    }
    setAnalysisMethod(method);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-1">Risk Register</h1>
          <p className="text-gray-600">Identify, assess, and manage project risks with AI-powered analysis</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add New Risk
        </button>
      </div>

      {showForm && (
        <RiskForm onClose={() => setShowForm(false)} onSave={handleCreateRisk} />
      )}

      {/* Risk Statistics */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-xs text-gray-600 mb-1">Total Risks</div>
          <div className="text-2xl">{riskStats.total}</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-xs text-red-700 mb-1">Critical</div>
          <div className="text-2xl text-red-700">{riskStats.critical}</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="text-xs text-orange-700 mb-1">High</div>
          <div className="text-2xl text-orange-700">{riskStats.high}</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="text-xs text-yellow-700 mb-1">Medium</div>
          <div className="text-2xl text-yellow-700">{riskStats.medium}</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-xs text-blue-700 mb-1">Open</div>
          <div className="text-2xl text-blue-700">{riskStats.open}</div>
        </div>
      </div>

      {/* Risk Heat Map */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">Risk Heat Map</h2>
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-300 border border-gray-300 rounded"></div>
              <span>Low</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-300 border border-gray-300 rounded"></div>
              <span>Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-400 border border-gray-300 rounded"></div>
              <span>High</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 border border-gray-300 rounded"></div>
              <span>Critical</span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-6 gap-2">
          {/* Header Row */}
          <div className="col-span-1"></div>
          <div className="text-center text-xs font-medium text-gray-700 py-2">Very Low</div>
          <div className="text-center text-xs font-medium text-gray-700 py-2">Low</div>
          <div className="text-center text-xs font-medium text-gray-700 py-2">Medium</div>
          <div className="text-center text-xs font-medium text-gray-700 py-2">High</div>
          <div className="text-center text-xs font-medium text-gray-700 py-2">Very High</div>
          
          {/* Very High Probability Row */}
          <div className="text-right text-xs font-medium text-gray-700 pr-3 flex items-center justify-end">Very High</div>
          <div className="h-20 bg-yellow-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-orange-400 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-red-400 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium text-white hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-red-500 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium text-white hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex flex-wrap gap-1 justify-center">
              {risks.filter(r => r.probability === 'High' && r.impact === 'High').map(r => (
                <span key={r.id} className="px-2 py-1 bg-white/30 backdrop-blur-sm rounded text-white font-mono text-xs">{r.id}</span>
              ))}
            </div>
          </div>
          <div className="h-20 bg-red-600 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium text-white hover:shadow-lg transition-shadow cursor-pointer"></div>
          
          {/* High Probability Row */}
          <div className="text-right text-xs font-medium text-gray-700 pr-3 flex items-center justify-end">High</div>
          <div className="h-20 bg-green-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-yellow-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex flex-wrap gap-1 justify-center">
              {risks.filter(r => r.probability === 'High' && r.impact === 'Medium').map(r => (
                <span key={r.id} className="px-2 py-1 bg-white/40 backdrop-blur-sm rounded font-mono text-xs">{r.id}</span>
              ))}
            </div>
          </div>
          <div className="h-20 bg-orange-400 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex flex-wrap gap-1 justify-center">
              {risks.filter(r => r.probability === 'Medium' && r.impact === 'High').map(r => (
                <span key={r.id} className="px-2 py-1 bg-white/40 backdrop-blur-sm rounded font-mono text-xs">{r.id}</span>
              ))}
            </div>
          </div>
          <div className="h-20 bg-red-400 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium text-white hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-red-500 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium text-white hover:shadow-lg transition-shadow cursor-pointer"></div>
          
          {/* Medium Probability Row */}
          <div className="text-right text-xs font-medium text-gray-700 pr-3 flex items-center justify-end">Medium</div>
          <div className="h-20 bg-green-200 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-green-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex flex-wrap gap-1 justify-center">
              {risks.filter(r => r.probability === 'Low' && r.impact === 'Medium').map(r => (
                <span key={r.id} className="px-2 py-1 bg-white/40 backdrop-blur-sm rounded font-mono text-xs">{r.id}</span>
              ))}
            </div>
          </div>
          <div className="h-20 bg-yellow-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex flex-wrap gap-1 justify-center">
              {risks.filter(r => r.probability === 'Medium' && r.impact === 'Medium').map(r => (
                <span key={r.id} className="px-2 py-1 bg-white/40 backdrop-blur-sm rounded font-mono text-xs">{r.id}</span>
              ))}
            </div>
          </div>
          <div className="h-20 bg-orange-400 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-red-400 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium text-white hover:shadow-lg transition-shadow cursor-pointer"></div>
          
          {/* Low Probability Row */}
          <div className="text-right text-xs font-medium text-gray-700 pr-3 flex items-center justify-end">Low</div>
          <div className="h-20 bg-green-100 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-green-200 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-green-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-yellow-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-orange-400 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          
          {/* Very Low Probability Row */}
          <div className="text-right text-xs font-medium text-gray-700 pr-3 flex items-center justify-end">Very Low</div>
          <div className="h-20 bg-green-50 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-green-100 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-green-200 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-green-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
          <div className="h-20 bg-yellow-300 border-2 border-gray-300 rounded-lg flex items-center justify-center text-xs font-medium hover:shadow-lg transition-shadow cursor-pointer"></div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-600">
            <span className="font-medium">Impact →</span>
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-medium">↑ Probability</span>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search risks by title, ID, or owner..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
        
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex gap-4">
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="mitigated">Mitigated</option>
              <option value="closed">Closed</option>
            </select>
            <select 
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Priority</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Risk List - Compact - 2/3 width */}
        <div className="col-span-2 space-y-3">
          <h2 className="text-lg flex items-center gap-2">
            Active Risks
            {selectedRisk && (
              <span className="text-sm text-gray-500">
                (Selected: {selectedRisk.id})
              </span>
            )}
          </h2>
          
          {risks.map((risk) => (
            <div 
              key={risk.id}
              onClick={() => setSelectedRisk(risk)}
              className={`bg-white rounded-lg border hover:shadow-md transition-all cursor-pointer ${
                selectedRisk?.id === risk.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${
                      risk.score >= 15 ? 'bg-red-100' :
                      risk.score >= 8 ? 'bg-orange-100' :
                      'bg-yellow-100'
                    }`}>
                      <AlertTriangle className={`w-4 h-4 ${
                        risk.score >= 15 ? 'text-red-600' :
                        risk.score >= 8 ? 'text-orange-600' :
                        'text-yellow-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-gray-600">{risk.id}</span>
                        <h3 className="font-medium text-sm truncate">{risk.title}</h3>
                      </div>
                      <p className="text-xs text-gray-600 mb-2 line-clamp-1">{risk.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs border ${getPriorityColor(risk.score)}`}>
                          {getPriorityLabel(risk.score)} ({risk.score})
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          risk.status === 'Open' ? 'bg-blue-100 text-blue-700' :
                          risk.status === 'Mitigated' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {risk.status}
                        </span>
                        <span className="text-xs text-gray-600">{risk.category}</span>
                        {risk.trend === 'up' && <TrendingUp className="w-3 h-3 text-red-600" />}
                        {risk.trend === 'down' && <TrendingDown className="w-3 h-3 text-green-600" />}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Risk Analysis Techniques - 1/3 width */}
        <div className="space-y-3">
          <h2 className="text-lg">AI-Powered Analysis</h2>

          {/* Selection Prompt */}
          {!selectedRisk && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-900 mb-1">
                    Select a Risk
                  </div>
                  <div className="text-xs text-amber-700">
                    Click on any risk to enable advanced risk analysis techniques
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FMEA Tool */}
          <div className={`bg-white rounded-lg border p-4 transition-all ${
            selectedRisk ? 'border-purple-200 shadow-sm' : 'border-gray-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-sm">FMEA Analysis</h3>
              <Sparkles className="w-4 h-4 text-purple-500 ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Failure Mode & Effects Analysis - Assess severity, occurrence, and detectability with RPN calculation
            </p>
            <button
              onClick={() => handleOpenAnalysis('fmea')}
              disabled={!selectedRisk}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedRisk 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {selectedRisk ? 'Analyze with AI' : 'Select Risk First'}
            </button>
          </div>

          {/* Monte Carlo Tool */}
          <div className={`bg-white rounded-lg border p-4 transition-all ${
            selectedRisk ? 'border-blue-200 shadow-sm' : 'border-gray-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <BarChart4 className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-sm">Monte Carlo</h3>
              <Sparkles className="w-4 h-4 text-purple-500 ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Probabilistic simulation - Run 10,000 iterations to quantify risk impact on schedule and budget
            </p>
            <button
              onClick={() => handleOpenAnalysis('montecarlo')}
              disabled={!selectedRisk}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedRisk 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {selectedRisk ? 'Run Simulation' : 'Select Risk First'}
            </button>
          </div>

          {/* Bow Tie Tool */}
          <div className={`bg-white rounded-lg border p-4 transition-all ${
            selectedRisk ? 'border-green-200 shadow-sm' : 'border-gray-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <GitMerge className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-sm">Bow Tie Analysis</h3>
              <Sparkles className="w-4 h-4 text-purple-500 ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Visualize causes, preventive controls, consequences, and mitigative controls
            </p>
            <button
              onClick={() => handleOpenAnalysis('bowtie')}
              disabled={!selectedRisk}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedRisk 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {selectedRisk ? 'Build Diagram' : 'Select Risk First'}
            </button>
          </div>

          {/* Selected Risk Info */}
          {selectedRisk && (
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-blue-600" />
                <div className="text-xs text-blue-700 font-medium">Selected for Analysis</div>
              </div>
              <div className="text-sm font-medium mb-1">{selectedRisk.id}</div>
              <div className="text-xs text-gray-700 mb-2">{selectedRisk.title}</div>
              <div className="pt-2 border-t border-blue-200">
                <div className="text-xs text-gray-600 mb-1">Probability: {selectedRisk.probability}</div>
                <div className="text-xs text-gray-600 mb-1">Impact: {selectedRisk.impact}</div>
                <div className="text-xs text-gray-600">Risk Score: {selectedRisk.score}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FMEA Modal */}
      {analysisMethod === 'fmea' && selectedRisk && (
        <FMEAAnalysis risk={selectedRisk} projectId={projectId} onClose={() => setAnalysisMethod(null)} />
      )}

      {/* Monte Carlo Modal */}
      {analysisMethod === 'montecarlo' && selectedRisk && (
        <MonteCarloSimulation risk={selectedRisk} projectId={projectId} onClose={() => setAnalysisMethod(null)} />
      )}

      {/* Bow Tie Modal */}
      {analysisMethod === 'bowtie' && selectedRisk && (
        <BowTieAnalysis risk={selectedRisk} projectId={projectId} onClose={() => setAnalysisMethod(null)} />
      )}
    </div>
  );
}

// Risk create form
function RiskForm({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    category: string;
    probability: string;
    impact: string;
    owner: string;
    targetDate: string;
    mitigation: string;
  }) => void;
}) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'Technical',
    probability: 'Medium',
    impact: 'Medium',
    owner: '',
    targetDate: '',
    mitigation: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  const previewScore = computeRiskScore(form.probability, form.impact);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl">Add New Risk</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm mb-2">Title *</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              placeholder="Short risk title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm mb-2">Description *</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              required
              placeholder="Describe the risk"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm mb-2">Category</label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>Technical</option>
                <option>Resource</option>
                <option>Compliance</option>
                <option>Financial</option>
                <option>Data</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2">Probability *</label>
              <select
                name="probability"
                value={form.probability}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {RISK_LEVELS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2">Impact *</label>
              <select
                name="impact"
                value={form.impact}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {RISK_LEVELS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Computed risk score: <span className="font-medium">{previewScore}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2">Owner</label>
              <OwnerSelect
                name="owner"
                value={form.owner}
                onChange={(v) => setForm({ ...form, owner: v })}
              />
            </div>
            <div>
              <label className="block text-sm mb-2">Target Date</label>
              <input
                type="date"
                name="targetDate"
                value={form.targetDate}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm mb-2">Mitigation</label>
            <textarea
              name="mitigation"
              value={form.mitigation}
              onChange={handleChange}
              rows={2}
              placeholder="Mitigation plan"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Risk
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// FMEA Analysis Component
function FMEAAnalysis({ risk, projectId, onClose }: { risk: Risk; projectId?: number; onClose: () => void }) {
  const [failureModes, setFailureModes] = useState([
    { id: 1, mode: '', severity: 0, occurrence: 0, detection: 0 },
    { id: 2, mode: '', severity: 0, occurrence: 0, detection: 0 },
    { id: 3, mode: '', severity: 0, occurrence: 0, detection: 0 },
    { id: 4, mode: '', severity: 0, occurrence: 0, detection: 0 },
  ]);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sprint 22 — hydrate any saved FMEA on mount so re-opening the modal
  // surfaces the user's last input instead of a blank scoresheet.
  useEffect(() => {
    const apiId = parseRaidId(risk.id);
    if (!projectId || apiId == null) return;
    loadAnalysis<{ state: typeof failureModes; saved_at: string }>(projectId, apiId, 'fmea')
      .then((saved) => {
        if (saved?.state && Array.isArray(saved.state)) {
          setFailureModes(saved.state);
        }
      })
      .catch((err) => {
        // Non-critical — modal still works with the default blank rows.
        console.warn('FMEAAnalysis loadAnalysis failed:', err);
      });
     
  }, [risk.id, projectId]);

  const handleSaveAnalysis = async () => {
    const apiId = parseRaidId(risk.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before persisting FMEA');
      return;
    }
    setSaving(true);
    try {
      await saveAnalysis(projectId, apiId, 'fmea', failureModes);
      toast.success('FMEA saved');
    } catch {
      toast.error('Failed to save FMEA');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await exportAnalysisPdf({
        elementId: 'fmea-analysis-pdf-root',
        filename: `fmea-${risk.id}`,
        title: `FMEA — ${risk.title}`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const updateFailureMode = (id: number, field: 'mode' | 'severity' | 'occurrence' | 'detection', value: string | number) => {
    setFailureModes(prev => prev.map(fm => 
      fm.id === id ? { ...fm, [field]: value } : fm
    ));
  };

  const calculateRPN = (severity: number, occurrence: number, detection: number) => {
    return severity * occurrence * detection;
  };

  const generateAIAnalysis = async () => {
    setAnalyzing(true);
    const numericId = parseRaidId(risk.id);
    try {
      if (numericId == null) throw new Error('Risk id missing');
      const result = await aiInsightsService.analyzeRisk(numericId);
      const sev = ({ High: 9, Medium: 6, Low: 3 } as Record<string, number>)[result.impact] ?? 6;
      const occ = ({ High: 8, Medium: 5, Low: 2 } as Record<string, number>)[result.probability] ?? 5;
      const det = 4;
      const modes = (result.suggested_actions || []).slice(0, 4).map((mode, idx) => ({
        id: idx + 1,
        mode,
        severity: Math.max(1, sev - idx),
        occurrence: Math.max(1, occ - idx),
        detection: det,
      }));
      while (modes.length < 4) {
        modes.push({ id: modes.length + 1, mode: '', severity: 0, occurrence: 0, detection: 0 });
      }
      setFailureModes(modes);
      setAiGenerated(true);
      setAnalyzing(false);
      toast.success('AI FMEA analysis generated');
      return;
    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed — using reference data');
    }
    setTimeout(() => {
      const aiFailureModes: Record<string, Array<{ mode: string; severity: number; occurrence: number; detection: number }>> = {
        'Technical': [
          { mode: 'Cloud infrastructure fails during peak migration period', severity: 9, occurrence: 6, detection: 4 },
          { mode: 'Data corruption during migration process', severity: 10, occurrence: 3, detection: 5 },
          { mode: 'Insufficient bandwidth causes migration timeout', severity: 7, occurrence: 5, detection: 3 },
          { mode: 'Security breach during data transfer', severity: 10, occurrence: 2, detection: 6 },
        ],
        'Resource': [
          { mode: 'Key developers reassigned to higher priority project', severity: 8, occurrence: 7, detection: 3 },
          { mode: 'Critical skill gap discovered mid-project', severity: 7, occurrence: 5, detection: 4 },
          { mode: 'Resource burnout due to overtime', severity: 6, occurrence: 6, detection: 5 },
          { mode: 'Contractor unavailability during critical phase', severity: 8, occurrence: 4, detection: 3 },
        ],
        'Compliance': [
          { mode: 'Vendor fails security audit requirements', severity: 8, occurrence: 4, detection: 3 },
          { mode: 'New regulations introduced mid-project', severity: 9, occurrence: 2, detection: 7 },
          { mode: 'Documentation gaps delay compliance approval', severity: 6, occurrence: 5, detection: 4 },
          { mode: 'Third-party dependency non-compliant', severity: 7, occurrence: 3, detection: 5 },
        ],
      };

      const categoryModes = aiFailureModes[risk.category] || aiFailureModes['Technical'];
      const newModes = categoryModes.map((fm, idx) => ({
        id: idx + 1,
        mode: fm.mode,
        severity: fm.severity,
        occurrence: fm.occurrence,
        detection: fm.detection,
      }));

      setFailureModes(newModes);
      setAiGenerated(true);
      setAnalyzing(false);
    }, 1500);
  };

  const sortedModes = [...failureModes]
    .filter(fm => fm.mode.trim())
    .map(fm => ({ ...fm, rpn: calculateRPN(fm.severity, fm.occurrence, fm.detection) }))
    .sort((a, b) => b.rpn - a.rpn);

  const getRPNColor = (rpn: number) => {
    if (rpn >= 200) return 'bg-red-100 text-red-700 border-red-300';
    if (rpn >= 100) return 'bg-orange-100 text-orange-700 border-orange-300';
    if (rpn >= 50) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-green-100 text-green-700 border-green-300';
  };

  const getRPNLabel = (rpn: number) => {
    if (rpn >= 200) return 'Critical - Immediate Action';
    if (rpn >= 100) return 'High - Priority Action';
    if (rpn >= 50) return 'Medium - Monitor';
    return 'Low - Acceptable';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl">FMEA - Failure Mode & Effects Analysis</h2>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI-Powered
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Analyzing: <span className="font-mono">{risk.id}</span> - {risk.title}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6" id="fmea-analysis-pdf-root">
          {/* AI Analysis Button */}
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h3 className="font-medium">AI FMEA Generator</h3>
                </div>
                <p className="text-sm text-gray-600">
                  AI identifies potential failure modes and calculates Risk Priority Number (RPN = Severity × Occurrence × Detection)
                </p>
              </div>
              <button
                onClick={generateAIAnalysis}
                disabled={analyzing}
                className="ml-4 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                {analyzing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate FMEA
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Risk Context */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Risk Event</div>
            <div className="text-lg font-medium mb-2">{risk.title}</div>
            <div className="text-sm text-gray-600 mb-2">{risk.description}</div>
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span>Category: {risk.category}</span>
              <span>•</span>
              <span>Probability: {risk.probability}</span>
              <span>•</span>
              <span>Impact: {risk.impact}</span>
              <span>•</span>
              <span>Risk Score: {risk.score}</span>
            </div>
          </div>

          {/* Rating Scale Reference */}
          <div className="mb-6 grid grid-cols-3 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="text-sm font-medium mb-2">Severity (1-10)</h4>
              <div className="text-xs space-y-1">
                <div>10: Catastrophic failure</div>
                <div>7-9: Serious impact</div>
                <div>4-6: Moderate impact</div>
                <div>1-3: Minor impact</div>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="text-sm font-medium mb-2">Occurrence (1-10)</h4>
              <div className="text-xs space-y-1">
                <div>10: Almost certain</div>
                <div>7-9: Frequent</div>
                <div>4-6: Occasional</div>
                <div>1-3: Rare</div>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="text-sm font-medium mb-2">Detection (1-10)</h4>
              <div className="text-xs space-y-1">
                <div>10: Cannot detect</div>
                <div>7-9: Low detectability</div>
                <div>4-6: Moderate</div>
                <div>1-3: High detectability</div>
              </div>
            </div>
          </div>

          {/* FMEA Table */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Failure Modes Analysis</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Potential Failure Mode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 w-24">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 w-24">Occurrence</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 w-24">Detection</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 w-24">RPN</th>
                  </tr>
                </thead>
                <tbody>
                  {failureModes.map((fm, idx) => {
                    const rpn = calculateRPN(fm.severity, fm.occurrence, fm.detection);
                    return (
                      <tr key={fm.id} className="border-b border-gray-200 bg-white">
                        <td className="px-4 py-3 text-sm text-gray-600">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={fm.mode}
                            onChange={(e) => updateFailureMode(fm.id, 'mode', e.target.value)}
                            placeholder="Describe potential failure mode..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={fm.severity || ''}
                            onChange={(e) => updateFailureMode(fm.id, 'severity', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={fm.occurrence || ''}
                            onChange={(e) => updateFailureMode(fm.id, 'occurrence', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={fm.detection || ''}
                            onChange={(e) => updateFailureMode(fm.id, 'detection', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className={`px-2 py-1 rounded border text-center font-medium text-sm ${getRPNColor(rpn)}`}>
                            {rpn > 0 ? rpn : '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Priority Ranking */}
          {sortedModes.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium mb-3">Priority Ranking (by RPN)</h3>
              <div className="space-y-3">
                {sortedModes.map((fm, idx) => (
                  <div key={fm.id} className={`border-2 rounded-lg p-4 ${getRPNColor(fm.rpn)}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">#{idx + 1}</span>
                          <span className="text-sm font-medium">{fm.mode}</span>
                        </div>
                        <div className="text-xs opacity-80">{getRPNLabel(fm.rpn)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{fm.rpn}</div>
                        <div className="text-xs opacity-80">RPN</div>
                      </div>
                    </div>
                    <div className="flex gap-6 text-xs">
                      <span>Severity: {fm.severity}/10</span>
                      <span>Occurrence: {fm.occurrence}/10</span>
                      <span>Detection: {fm.detection}/10</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {aiGenerated && sortedModes.length > 0 && (
            <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-900">AI Recommendations</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>Top Priority:</strong> Address "{sortedModes[0]?.mode}" (RPN: {sortedModes[0]?.rpn}) immediately</p>
                <p><strong>Actions:</strong> Focus on reducing either Severity, Occurrence, or improving Detection</p>
                <p><strong>Target RPN:</strong> Aim to reduce all failure modes to RPN &lt; 100</p>
                <p><strong>Review Cycle:</strong> Re-evaluate FMEA after implementing mitigation controls</p>
              </div>
            </div>
          )}

          {/* Sprint 22 — restored as real buttons (Save persists to
              raid_item.extra_metadata.analyses.fmea; Export PDF
              renders the modal via jsPDF+html2canvas; AI uses the
              existing analyzeRisk endpoint via generateAIAnalysis
              above — already wired further up in the modal). */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSaveAnalysis}
              disabled={saving || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save FMEA'}
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'Export as PDF'}
            </button>
            <button onClick={onClose} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Monte Carlo Simulation Component
function MonteCarloSimulation({ risk, projectId, onClose }: { risk: Risk; projectId?: number; onClose: () => void }) {
  const [simulating, setSimulating] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [parameters, setParameters] = useState({
    scheduleImpact: { min: 0, mostLikely: 0, max: 0 }, // in days
    budgetImpact: { min: 0, mostLikely: 0, max: 0 }, // in thousands
    iterations: 10000,
  });
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sprint 22 — restore last run on open. Persists both parameters
  // and results so the user lands on the chart they generated last time.
  useEffect(() => {
    const apiId = parseRaidId(risk.id);
    if (!projectId || apiId == null) return;
    loadAnalysis<{ state: { parameters: typeof parameters; results: any }; saved_at: string }>(
      projectId, apiId, 'montecarlo',
    )
      .then((saved) => {
        if (saved?.state?.parameters) setParameters(saved.state.parameters);
        if (saved?.state?.results) setResults(saved.state.results);
      })
      .catch((err) => console.warn('MonteCarloSimulation loadAnalysis failed:', err));
     
  }, [risk.id, projectId]);

  const handleSaveResults = async () => {
    const apiId = parseRaidId(risk.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before persisting simulation');
      return;
    }
    setSaving(true);
    try {
      await saveAnalysis(projectId, apiId, 'montecarlo', { parameters, results });
      toast.success('Simulation saved');
    } catch {
      toast.error('Failed to save simulation');
    } finally {
      setSaving(false);
    }
  };

  const handleExportReport = async () => {
    setExporting(true);
    try {
      await exportAnalysisPdf({
        elementId: 'montecarlo-pdf-root',
        filename: `montecarlo-${risk.id}`,
        title: `Monte Carlo — ${risk.title}`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const runSimulation = () => {
    setSimulating(true);
    setTimeout(() => {
      // AI-generated parameters based on risk characteristics
      const aiParams: Record<string, any> = {
        'Technical': {
          scheduleImpact: { min: 5, mostLikely: 15, max: 30 },
          budgetImpact: { min: 50, mostLikely: 150, max: 350 },
        },
        'Resource': {
          scheduleImpact: { min: 10, mostLikely: 20, max: 45 },
          budgetImpact: { min: 75, mostLikely: 200, max: 400 },
        },
        'Compliance': {
          scheduleImpact: { min: 7, mostLikely: 14, max: 28 },
          budgetImpact: { min: 30, mostLikely: 100, max: 250 },
        },
      };

      const params = aiParams[risk.category] || aiParams['Technical'];
      setParameters(prev => ({ ...prev, ...params }));

      // Simulated results using triangular distribution
      const simulatedResults = {
        schedule: {
          mean: params.scheduleImpact.mostLikely + (params.scheduleImpact.max - params.scheduleImpact.min) * 0.1,
          p10: params.scheduleImpact.min + 1,
          p50: params.scheduleImpact.mostLikely,
          p90: params.scheduleImpact.max - 2,
          min: params.scheduleImpact.min,
          max: params.scheduleImpact.max,
        },
        budget: {
          mean: params.budgetImpact.mostLikely + (params.budgetImpact.max - params.budgetImpact.min) * 0.15,
          p10: params.budgetImpact.min + 10,
          p50: params.budgetImpact.mostLikely,
          p90: params.budgetImpact.max - 20,
          min: params.budgetImpact.min,
          max: params.budgetImpact.max,
        },
        probability: {
          scheduleOverrun: 72,
          budgetOverrun: 68,
          bothOverrun: 45,
        },
      };

      setResults(simulatedResults);
      setSimulating(false);
    }, 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl">Monte Carlo Simulation</h2>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                10,000 Iterations
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Analyzing: <span className="font-mono">{risk.id}</span> - {risk.title}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6" id="montecarlo-pdf-root">
          {/* Risk Context */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Risk Event</div>
            <div className="text-lg font-medium mb-2">{risk.title}</div>
            <div className="text-sm text-gray-600">{risk.description}</div>
          </div>

          {/* Run Simulation */}
          {!results && (
            <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
              <div className="text-center">
                <BarChart4 className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Probabilistic Risk Quantification</h3>
                <p className="text-sm text-gray-600 mb-6 max-w-2xl mx-auto">
                  AI will run 10,000 Monte Carlo iterations using triangular distribution to quantify the impact of this risk on project schedule and budget
                </p>
                <button
                  onClick={runSimulation}
                  disabled={simulating}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 font-medium flex items-center gap-3 mx-auto"
                >
                  {simulating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Running 10,000 Simulations...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Run Monte Carlo Simulation
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Simulation Results */}
          {results && (
            <>
              {/* Summary Stats */}
              <div className="mb-6 grid grid-cols-2 gap-4">
                {/* Schedule Impact */}
                <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-300 rounded-lg p-4">
                  <h3 className="font-medium text-orange-900 mb-4">Schedule Impact (Days)</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Mean (Average):</span>
                      <span className="font-medium">{results.schedule.mean.toFixed(1)} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Median (P50):</span>
                      <span className="font-medium">{results.schedule.p50} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Best Case (P10):</span>
                      <span className="font-medium text-green-700">{results.schedule.p10} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Worst Case (P90):</span>
                      <span className="font-medium text-red-700">{results.schedule.p90} days</span>
                    </div>
                    <div className="pt-3 border-t border-orange-200">
                      <div className="text-xs text-gray-600">Range: {results.schedule.min} - {results.schedule.max} days</div>
                    </div>
                  </div>
                </div>

                {/* Budget Impact */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-4">
                  <h3 className="font-medium text-green-900 mb-4">Budget Impact ($000s)</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Mean (Average):</span>
                      <span className="font-medium">${results.budget.mean.toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Median (P50):</span>
                      <span className="font-medium">${results.budget.p50}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Best Case (P10):</span>
                      <span className="font-medium text-green-700">${results.budget.p10}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">Worst Case (P90):</span>
                      <span className="font-medium text-red-700">${results.budget.p90}K</span>
                    </div>
                    <div className="pt-3 border-t border-green-200">
                      <div className="text-xs text-gray-600">Range: ${results.budget.min}K - ${results.budget.max}K</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribution Visualization */}
              <div className="mb-6">
                <h3 className="font-medium mb-3">Probability Distribution</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Schedule Distribution */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium mb-3">Schedule Impact Distribution</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="text-xs w-20">P10 ({results.schedule.p10}d)</div>
                        <div className="flex-1 h-6 bg-green-200 rounded" style={{ width: '30%' }}></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs w-20">P50 ({results.schedule.p50}d)</div>
                        <div className="flex-1 h-6 bg-yellow-300 rounded" style={{ width: '70%' }}></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs w-20">P90 ({results.schedule.p90}d)</div>
                        <div className="flex-1 h-6 bg-red-400 rounded" style={{ width: '95%' }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Budget Distribution */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium mb-3">Budget Impact Distribution</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="text-xs w-20">P10 (${results.budget.p10}K)</div>
                        <div className="flex-1 h-6 bg-green-200 rounded" style={{ width: '25%' }}></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs w-20">P50 (${results.budget.p50}K)</div>
                        <div className="flex-1 h-6 bg-yellow-300 rounded" style={{ width: '65%' }}></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs w-20">P90 (${results.budget.p90}K)</div>
                        <div className="flex-1 h-6 bg-red-400 rounded" style={{ width: '90%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Probability Analysis */}
              <div className="mb-6 bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                <h3 className="font-medium text-purple-900 mb-4">Probability of Overruns</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-700">{results.probability.scheduleOverrun}%</div>
                    <div className="text-xs text-gray-600 mt-1">Schedule Overrun</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-700">{results.probability.budgetOverrun}%</div>
                    <div className="text-xs text-gray-600 mt-1">Budget Overrun</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-700">{results.probability.bothOverrun}%</div>
                    <div className="text-xs text-gray-600 mt-1">Both Overrun</div>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-blue-900">AI Recommendations</h3>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  <p><strong>Schedule Reserve:</strong> Add {results.schedule.p90} days contingency (P90 confidence)</p>
                  <p><strong>Budget Reserve:</strong> Allocate ${results.budget.p90}K contingency budget</p>
                  <p><strong>Risk Response:</strong> {results.probability.bothOverrun}% chance of dual impact - prioritize mitigation</p>
                  <p><strong>Monitoring:</strong> Track early warning indicators to stay within P50 estimates</p>
                </div>
              </div>

              {/* Sprint 22 — restored Save Results + Export Report as
                  real actions (extra_metadata.analyses.montecarlo
                  blob + jsPDF capture). */}
              <div className="flex gap-3">
                <button
                  onClick={() => setResults(null)}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                >
                  Run New Simulation
                </button>
                <button
                  type="button"
                  onClick={handleSaveResults}
                  disabled={saving || !projectId}
                  title={!projectId ? 'Save the project first' : undefined}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save Results'}
                </button>
                <button
                  type="button"
                  onClick={handleExportReport}
                  disabled={exporting}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileDown className="w-4 h-4" />
                  {exporting ? 'Exporting…' : 'Export Report'}
                </button>
                <button onClick={onClose} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Bow Tie Analysis Component
function BowTieAnalysis({ risk, projectId, onClose }: { risk: Risk; projectId?: number; onClose: () => void }) {
  const [causes, setCauses] = useState(['', '', '']);
  const [preventiveControls, setPreventiveControls] = useState(['', '', '']);
  const [consequences, setConsequences] = useState(['', '', '']);
  const [mitigativeControls, setMitigativeControls] = useState(['', '', '']);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sprint 22 — pre-load saved Bow Tie inputs on open.
  useEffect(() => {
    const apiId = parseRaidId(risk.id);
    if (!projectId || apiId == null) return;
    loadAnalysis<{ state: { causes: string[]; preventiveControls: string[]; consequences: string[]; mitigativeControls: string[] }; saved_at: string }>(
      projectId, apiId, 'bowtie',
    )
      .then((saved) => {
        if (!saved?.state) return;
        if (Array.isArray(saved.state.causes)) setCauses(saved.state.causes);
        if (Array.isArray(saved.state.preventiveControls)) setPreventiveControls(saved.state.preventiveControls);
        if (Array.isArray(saved.state.consequences)) setConsequences(saved.state.consequences);
        if (Array.isArray(saved.state.mitigativeControls)) setMitigativeControls(saved.state.mitigativeControls);
      })
      .catch((err) => console.warn('BowTieAnalysis loadAnalysis failed:', err));
     
  }, [risk.id, projectId]);

  const handleSaveBowTie = async () => {
    const apiId = parseRaidId(risk.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before persisting Bow Tie');
      return;
    }
    setSaving(true);
    try {
      await saveAnalysis(projectId, apiId, 'bowtie', {
        causes, preventiveControls, consequences, mitigativeControls,
      });
      toast.success('Bow Tie saved');
    } catch {
      toast.error('Failed to save Bow Tie');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await exportAnalysisPdf({
        elementId: 'bowtie-pdf-root',
        filename: `bowtie-${risk.id}`,
        title: `Bow Tie — ${risk.title}`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const generateAIAnalysis = async () => {
    setAnalyzing(true);
    const numericId = parseRaidId(risk.id);
    try {
      if (numericId == null) throw new Error('Risk id missing');
      const result = await aiInsightsService.analyzeRisk(numericId);
      const acts = result.suggested_actions || [];
      if (acts.length >= 3) {
        setCauses([acts[0] || '', acts[1] || '', acts[2] || '']);
        setPreventiveControls([acts[3] || acts[0] || '', acts[4] || acts[1] || '', acts[5] || acts[2] || '']);
        setConsequences([result.content || result.reasoning, '', '']);
        setMitigativeControls([acts[acts.length - 1] || '', '', '']);
        setAiGenerated(true);
        setAnalyzing(false);
        toast.success('AI Bow-Tie analysis generated');
        return;
      }
    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed — using reference data');
    }
    setTimeout(() => {
      const aiData: Record<string, any> = {
        'Technical': {
          causes: [
            'Insufficient cloud capacity planning',
            'Peak migration period scheduling conflict',
            'Vendor infrastructure limitations'
          ],
          preventive: [
            'Conduct capacity assessment with vendor',
            'Implement phased migration approach',
            'Establish backup vendor agreement'
          ],
          consequences: [
            'Project schedule delay of 2-4 weeks',
            'Additional costs for expedited resources',
            'Stakeholder confidence erosion'
          ],
          mitigative: [
            'Activate secondary vendor immediately',
            'Re-baseline schedule with stakeholders',
            'Increase communication frequency'
          ],
        },
        'Resource': {
          causes: [
            'Competing project priorities in Q2',
            'Resource allocation not formalized',
            'Lack of resource backup plan'
          ],
          preventive: [
            'Secure executive resource commitments',
            'Document resource allocation agreements',
            'Identify and train backup resources'
          ],
          consequences: [
            'Critical path activities delayed',
            'Quality issues from understaffing',
            'Team morale and burnout risks'
          ],
          mitigative: [
            'Engage contractors immediately',
            'Reduce scope or extend timeline',
            'Escalate to PMO for intervention'
          ],
        },
        'Compliance': {
          causes: [
            'Incomplete vendor security documentation',
            'New regulatory requirements introduced',
            'Audit process timeline uncertainty'
          ],
          preventive: [
            'Pre-qualify vendors on security criteria',
            'Engage security team early in process',
            'Build buffer time for compliance review'
          ],
          consequences: [
            'Vendor onboarding delayed 2-3 weeks',
            'Contract renegotiation required',
            'Potential regulatory penalties'
          ],
          mitigative: [
            'Use pre-approved vendor alternatives',
            'Fast-track compliance review process',
            'Implement temporary workarounds'
          ],
        },
      };

      const data = aiData[risk.category] || aiData['Technical'];
      setCauses(data.causes);
      setPreventiveControls(data.preventive);
      setConsequences(data.consequences);
      setMitigativeControls(data.mitigative);
      setAiGenerated(true);
      setAnalyzing(false);
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl">Bow Tie Analysis</h2>
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI-Powered
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Analyzing: <span className="font-mono">{risk.id}</span> - {risk.title}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6" id="bowtie-pdf-root">
          {/* AI Analysis Button */}
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium">AI Bow Tie Generator</h3>
                </div>
                <p className="text-sm text-gray-600">
                  AI identifies causes, preventive controls, consequences, and mitigative controls for comprehensive risk visualization
                </p>
              </div>
              <button
                onClick={generateAIAnalysis}
                disabled={analyzing}
                className="ml-4 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                {analyzing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Bow Tie
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Bow Tie Diagram */}
          <div className="mb-6">
            <div className="grid grid-cols-5 gap-4">
              {/* Causes */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-red-700 mb-3">Threat/Causes</h3>
                {causes.map((cause, idx) => (
                  <textarea
                    key={idx}
                    value={cause}
                    onChange={(e) => {
                      const newCauses = [...causes];
                      newCauses[idx] = e.target.value;
                      setCauses(newCauses);
                    }}
                    placeholder={`Cause ${idx + 1}...`}
                    className="w-full px-3 py-2 border-2 border-red-200 bg-red-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    rows={3}
                  />
                ))}
              </div>

              {/* Preventive Controls */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-blue-700 mb-3">Preventive Controls</h3>
                {preventiveControls.map((control, idx) => (
                  <textarea
                    key={idx}
                    value={control}
                    onChange={(e) => {
                      const newControls = [...preventiveControls];
                      newControls[idx] = e.target.value;
                      setPreventiveControls(newControls);
                    }}
                    placeholder={`Control ${idx + 1}...`}
                    className="w-full px-3 py-2 border-2 border-blue-200 bg-blue-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={3}
                  />
                ))}
              </div>

              {/* Risk Event (Center) */}
              <div className="flex items-center justify-center">
                <div className="bg-gradient-to-br from-orange-100 to-red-100 border-4 border-orange-400 rounded-xl p-6 text-center">
                  <div className="text-xs font-medium text-orange-800 mb-2">RISK EVENT</div>
                  <div className="font-medium text-sm text-orange-900 mb-2">{risk.title}</div>
                  <div className="text-xs text-orange-700">Score: {risk.score}</div>
                </div>
              </div>

              {/* Mitigative Controls */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-green-700 mb-3">Mitigative Controls</h3>
                {mitigativeControls.map((control, idx) => (
                  <textarea
                    key={idx}
                    value={control}
                    onChange={(e) => {
                      const newControls = [...mitigativeControls];
                      newControls[idx] = e.target.value;
                      setMitigativeControls(newControls);
                    }}
                    placeholder={`Control ${idx + 1}...`}
                    className="w-full px-3 py-2 border-2 border-green-200 bg-green-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    rows={3}
                  />
                ))}
              </div>

              {/* Consequences */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-purple-700 mb-3">Consequences</h3>
                {consequences.map((consequence, idx) => (
                  <textarea
                    key={idx}
                    value={consequence}
                    onChange={(e) => {
                      const newConsequences = [...consequences];
                      newConsequences[idx] = e.target.value;
                      setConsequences(newConsequences);
                    }}
                    placeholder={`Consequence ${idx + 1}...`}
                    className="w-full px-3 py-2 border-2 border-purple-200 bg-purple-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    rows={3}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Bow Tie Methodology</h3>
            <div className="grid grid-cols-4 gap-4 text-xs">
              <div>
                <div className="font-medium text-red-700 mb-1">← Threat Side (Left)</div>
                <div className="text-gray-600">Focus on PREVENTION - stop the risk event from occurring</div>
              </div>
              <div>
                <div className="font-medium text-blue-700 mb-1">Preventive Barriers</div>
                <div className="text-gray-600">Proactive controls to reduce likelihood</div>
              </div>
              <div>
                <div className="font-medium text-green-700 mb-1">Mitigative Barriers</div>
                <div className="text-gray-600">Reactive controls to reduce impact</div>
              </div>
              <div>
                <div className="font-medium text-purple-700 mb-1">Consequence Side (Right) →</div>
                <div className="text-gray-600">Focus on MITIGATION - minimize impact if risk occurs</div>
              </div>
            </div>
          </div>

          {/* Control Effectiveness */}
          {aiGenerated && (
            <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-900">Control Effectiveness Assessment</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium mb-2">Preventive Controls Strength</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600 rounded-full" style={{ width: '70%' }}></div>
                    </div>
                    <span className="text-sm font-medium">70%</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Good prevention - likelihood reduced</p>
                </div>
                <div>
                  <div className="text-sm font-medium mb-2">Mitigative Controls Strength</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-green-600 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                    <span className="text-sm font-medium">65%</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Moderate mitigation - impact can be reduced</p>
                </div>
              </div>
            </div>
          )}

          {/* Sprint 22 — restored Save Bow Tie + Export as PDF as
              real actions (extra_metadata.analyses.bowtie blob +
              jsPDF capture). AI is reachable via the top "Generate
              AI Bow Tie" button which calls analyzeRisk. */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSaveBowTie}
              disabled={saving || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Bow Tie'}
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'Export as PDF'}
            </button>
            <button onClick={onClose} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
